import { useEffect, ReactElement, useState, useCallback, useMemo } from 'react';
import { createFilterOptions, TextField, Autocomplete, CircularProgress, Paper, Box, FormControlLabel, Divider, Checkbox, Chip } from '@mui/material';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import { autorun } from 'mobx';

import { Iconify } from '~/components/iconify';
import { ValueLabelPair } from '~/mst/models/abstract/collection_types';
import useLocales from '~/hooks/use_locales';
import type { AutocompleteProps, FieldProps, TagsMap, Value } from './types';

/** Prefix that triggers the tag picker inside the dropdown search. */
const TAG_PREFIX = '@';

type TagOption = {
  value: string;
  label: string;
  isTag: true;
  tagValues: Array<string | number>;
  count: number;
};

/**
 * A pseudo-option that toggles a set of real options: picking it selects all of them, or
 * deselects them when they're all selected already. `caption` is shown at the end of its row.
 */
export type GroupOption = {
  value: string;
  label: string;
  isGroup: true;
  groupValues: Array<string | number>;
  caption?: string;
  model?: any;
};

export type Props = {
  allowSelectAll?: boolean;
  tags?: TagsMap;
  renderOption?: (option: ValueLabelPair) => ReactElement;
  filterText?: (option: ValueLabelPair) => string;
  renderInput?: (option: ValueLabelPair) => ReactElement;
  InputStartAdornment?: (option: ValueLabelPair) => ReactElement;
} & FieldProps &
  AutocompleteProps<Value>;

function PaperComponent(paperProps) {
  const { t } = useLocales();
  const { children, handleSelectAll, selectAll, ...restPaperProps } = paperProps;

  return (
    <Paper {...restPaperProps}>
      <Box
        onMouseDown={(e) => e.preventDefault()}
        sx={{
          pl: 1.5,
          py: 0.5
        }}
      >
        <FormControlLabel
          label={t('base.labels.select_all')}
          onClick={(e) => {
            e.preventDefault();
            handleSelectAll();
          }}
          control={<Checkbox id="select-all-checkbox-for-autocomplete" checked={selectAll} />}
        />
      </Box>
      <Divider />
      {children}
    </Paper>
  );
}

export default function MuiAutocomplete(props: Props) {
  const {
    name,
    label,
    disabled,
    value,
    multiple,
    allowSelectAll,
    tags,
    InputStartAdornment,
    renderOption,
    renderInput,
    onInputChange,
    onChange,
    filterText,
    freeSolo,
    loading,
    model,
    error,
    helperText,
    options,
    ...rest
  } = props;
  const [selected, setSelected] = useState<boolean>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const handleSelectAll = useCallback(() => {
    setSelectAll((prev) => {
      if (!prev) onChange(options.filter((option) => !option?.isGroup));
      else onChange([]);
      return !prev;
    });
  }, [onChange, options]);

  // Pseudo-options representing tags — each expands to a set of real option values on select.
  const tagOptions = useMemo<TagOption[]>(
    () =>
      Object.entries(tags || {}).map(([tagName, tagValues]) => ({
        value: `${TAG_PREFIX}${tagName}`,
        label: `${TAG_PREFIX}${tagName}`,
        isTag: true,
        tagValues,
        count: tagValues.length
      })),
    [tags]
  );

  const defaultFilter = useMemo(() => createFilterOptions({ stringify: filterText }), [filterText]);
  // When the search starts with `@`, surface matching tags; otherwise filter the real options as usual.
  const filterOptions = useCallback(
    (opts, state) => {
      if (multiple && tagOptions.length && state.inputValue.startsWith(TAG_PREFIX)) {
        const query = state.inputValue.toLowerCase();
        return tagOptions.filter((option) => option.label.toLowerCase().includes(query));
      }
      return defaultFilter(opts, state);
    },
    [multiple, tagOptions, defaultFilter]
  );

  // Replace any picked tag pseudo-option with its real options (looked up by value), toggle the
  // options of a picked group, then dedupe.
  const expandTags = useCallback(
    (picked: any[]) => {
      const findOptions = (values: Array<string | number>) => values.map((v) => options.find((o) => o.value === v)).filter(Boolean);
      const group = picked.find((option) => option?.isGroup) as GroupOption | undefined;
      const rest = picked.filter((option) => !option?.isGroup);
      if (group) {
        const groupValues = new Set(group.groupValues);
        const allSelected = group.groupValues.every((v) => rest.some((option) => option?.value === v));
        return allSelected ? rest.filter((option) => !groupValues.has(option?.value)) : uniqBy([...rest, ...findOptions(group.groupValues)], 'value');
      }
      const expanded = rest.flatMap((option) => (option?.isTag ? findOptions(option.tagValues as Array<string | number>) : [option]));
      return uniqBy(expanded, 'value');
    },
    [options]
  );

  useEffect(
    () =>
      autorun(() => {
        if (multiple) {
          return setSelected(
            get(value, 'value', Array.isArray(value) ? value : []).map((option: ValueLabelPair | string) => {
              if (option?.value !== undefined) {
                return option;
              }
              return options.find((o) => o.value === option);
            })
          );
        }
        if (value?.value !== undefined) {
          return setSelected(value);
        }
        const option = options.find((o) => {
          if (value == null && o?.value == null) {
            return true;
          }
          return o?.value === value;
        });
        if (freeSolo) {
          return setSelected(option || value || { value: null, label: '' });
        }
        if (loading) {
          return setSelected({ value: null, label: '' });
        }
        return setSelected(option || { value: null, label: '' });
      }),
    [multiple, options, value, freeSolo, loading]
  );

  return (
    <Autocomplete
      disabled={disabled}
      value={selected}
      multiple={multiple}
      filterOptions={filterOptions}
      onChange={(_e, option, reason) => {
        if (reason === 'clear' || reason === 'removeOption') setSelectAll(false);
        if (multiple) {
          const values = expandTags(option as any[]);
          if (reason === 'selectOption' && allowSelectAll && values.length === options.filter((o) => !o?.isGroup).length) setSelectAll(true);
          onChange(values);
          return;
        }
        if (reason === 'selectOption' && allowSelectAll && selected.length === options.length) setSelectAll(true);
        // freeSolo + autoSelect can return the label string instead of the option object on blur
        if (typeof option === 'string') {
          const matchedOption = options.find((o) => o.label === option);
          if (matchedOption) {
            onChange(matchedOption.value);
            return;
          }
        }
        onChange(Object.hasOwn(option, 'value') ? option.value : option);
      }}
      getOptionLabel={(option) => {
        if (option) {
          return Object.hasOwn(option, 'label') ? option?.label : option;
        }
        return null;
      }}
      getOptionDisabled={() => {
        if (multiple && selected.some((option) => option?.value === 'all')) {
          return true;
        }
        return false;
      }}
      disableClearable={!multiple}
      renderOption={(propsInner, option = { label: '', value: null }) => {
        if (option?.isTag) {
          return (
            <li {...propsInner} key={option.value} data-testid={`${name}Tag${capitalize(option.value?.toString() || '')}`}>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                <Iconify icon="eva:pricetags-outline" width={16} height={16} />
                <span>{option.label}</span>
                <Box component="span" sx={{ ml: 'auto', opacity: 0.6 }}>
                  {option.count}
                </Box>
              </Box>
            </li>
          );
        }
        if (option?.isGroup) {
          const selectedValues = new Set((selected || []).map((o) => o?.value));
          const count = option.groupValues.filter((v) => selectedValues.has(v)).length;
          return (
            <li {...propsInner} key={option.value} data-testid={`${name}Group${capitalize(option.label)}`}>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Checkbox
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                  checked={count > 0 && count === option.groupValues.length}
                  indeterminate={count > 0 && count < option.groupValues.length}
                />
                <span>{option.label}</span>
                {option.caption && (
                  <Box component="span" sx={{ ml: 'auto', pl: 2, opacity: 0.6, whiteSpace: 'nowrap' }}>
                    {option.caption}
                  </Box>
                )}
              </Box>
            </li>
          );
        }
        return (
          <li {...propsInner} key={option.value} data-testid={`${name}Option${capitalize(option.value?.toString() || 'Null')}`}>
            <span style={{ ...(option?.removed && { textDecoration: 'line-through' }) }}>{renderOption ? renderOption(option) : option.label}</span>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          helperText={helperText}
          label={label}
          onChange={(e) => {
            params.slotProps.htmlInput.onChange(e);
            if (onInputChange) {
              onInputChange(e);
            }
          }}
          slotProps={{
            ...params.slotProps,

            input: {
              ...params.slotProps.input,
              ...(InputStartAdornment && { startAdornment: <InputStartAdornment model={model || selected?.model} /> }),
              endAdornment: (
                <>
                  {loading && <CircularProgress color="info" size={18} />}
                  {params.slotProps.input.endAdornment}
                  {renderInput && renderInput(selected)}
                </>
              )
            }
          }}
        />
      )}
      popupIcon={<Iconify icon="ep:arrow-down-bold" width={14} height={14} />}
      // renderValue also runs for a single value, which keeps the default input text
      {...(multiple && {
        renderValue: (values, getItemProps) => (
          <Box sx={{ maxHeight: (theme) => theme.spacing(16), overflowY: 'auto' }}>
            {values.map((option, index) => (
              <Chip {...getItemProps({ index })} key={option?.value} label={option?.label} />
            ))}
          </Box>
        )
      })}
      {...rest}
      freeSolo={freeSolo}
      options={options}
      {...(allowSelectAll && {
        slots: { paper: PaperComponent },
        slotProps: { paper: { selectAll, handleSelectAll } }
      })}
    />
  );
}
